import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { POLES, poleLabel } from "@/lib/poles";
import DiffHistory from "@/components/DiffHistory";
import ImageUpload from "@/components/ImageUpload";
import CompanyLogo from "@/components/CompanyLogo";
import CountrySelect from "@/components/CountrySelect";
import MemberActivationTimeline from "@/components/admin/MemberActivationTimeline";
import InvoicesList from "@/components/InvoicesList";
import {
  ArrowLeft, Building2, Users, Calendar, MessageSquare, Send, Trash2,
  Mail, Phone, MapPin, Globe, Plus, Activity, Clock, UserPlus, Briefcase, Tag, Save, Edit3, X,
  PhoneCall, CalendarPlus, Sparkles, FileText, CheckSquare, Mail as MailIcon,
} from "lucide-react";

const COMMENT_KINDS = [
  { id: "note", label: "Note", color: "neutral" },
  { id: "call", label: "Appel", color: "blue" },
  { id: "email", label: "Email", color: "amber" },
  { id: "meeting", label: "Rencontre", color: "emerald" },
  { id: "status_change", label: "Changement statut", color: "wine" },
];

export default function AdminCompanyDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [comment, setComment] = useState({ text: "", kind: "note" });
  const [categories, setCategories] = useState([]);
  const [editAttrs, setEditAttrs] = useState({ pole: "", category: "" });
  const [savingAttrs, setSavingAttrs] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [projectsCatalog, setProjectsCatalog] = useState([]);
  const [newAct, setNewAct] = useState({ type: "call", label: "", notes: "", at: new Date().toISOString().slice(0, 16) });

  // Sprint 92al — Load the projects catalog for the editor's multi-select.
  useEffect(() => {
    api.get("/admin/settings/projects").then((r) => setProjectsCatalog(r.data || [])).catch(() => {});
  }, []);

  const ACTIVITY_TYPES = [
    { id: "call", label: "Appel", icon: PhoneCall, color: "bg-sky-100 text-sky-700" },
    { id: "meeting", label: "RDV", icon: CalendarPlus, color: "bg-emerald-100 text-emerald-700" },
    { id: "activation", label: "Activation", icon: Sparkles, color: "bg-amber-100 text-amber-700" },
    { id: "email", label: "Email", icon: MailIcon, color: "bg-violet-100 text-violet-700" },
    { id: "demo", label: "Démo", icon: Activity, color: "bg-rose-100 text-rose-700" },
    { id: "task", label: "Tâche", icon: CheckSquare, color: "bg-neutral-200 text-neutral-700" },
    { id: "note", label: "Note", icon: FileText, color: "bg-neutral-100 text-neutral-700" },
    { id: "other", label: "Autre", icon: Activity, color: "bg-neutral-100 text-neutral-700" },
  ];
  const typeMeta = (t) => ACTIVITY_TYPES.find((x) => x.id === t) || ACTIVITY_TYPES[6];

  const addManualActivity = async (e) => {
    e.preventDefault();
    if (!newAct.label.trim()) { toast.error("Label requis"); return; }
    try {
      const payload = {
        type: newAct.type,
        label: newAct.label.trim(),
        notes: newAct.notes.trim() || undefined,
        at: newAct.at ? new Date(newAct.at).toISOString() : undefined,
      };
      await api.post(`/admin/companies/${id}/activities`, payload);
      toast.success("Activité ajoutée");
      setNewAct({ type: "call", label: "", notes: "", at: new Date().toISOString().slice(0, 16) });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  const deleteManualActivity = async (aid) => {
    if (!window.confirm("Supprimer cette activité ?")) return;
    try {
      await api.delete(`/admin/companies/${id}/activities/${aid}`);
      toast.success("Supprimée");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  const load = () => api.get(`/admin/companies/${id}/detail`).then((r) => {
    setData(r.data);
    setEditAttrs({ pole: r.data.company.pole || "", category: r.data.company.category || "" });
  }).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { api.get("/admin/settings/categories").then((r) => setCategories(r.data || [])).catch(() => {}); }, []);

  const openEditProfile = () => {
    const c = data?.company || {};
    setEditingProfile({
      name: c.name || "", legal_name: c.legal_name || "", siren: c.siren || "", vat_number: c.vat_number || "",
      website: c.website || "", email: c.email || "", phone: c.phone || "",
      city: c.city || "", country: c.country || "", description: c.description || "",
      tagline: c.tagline || "", logo: c.logo || "", banner: c.banner || "", status: c.status || "active",
      tags: c.tags || [], project_ids: c.project_ids || [],
    });
  };

  const saveProfile = async () => {
    if (!editingProfile) return;
    try {
      await api.patch(`/companies/${id}`, editingProfile);
      toast.success("Fiche entreprise mise à jour");
      setEditingProfile(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    }
  };

  const saveAttrs = async () => {
    setSavingAttrs(true);
    try {
      await api.patch(`/companies/${id}`, { pole: editAttrs.pole, category: editAttrs.category });
      toast.success("Pôle & Catégorie mis à jour");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur");
    } finally {
      setSavingAttrs(false);
    }
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!comment.text.trim()) return;
    try {
      await api.post(`/admin/companies/${id}/comments`, comment);
      setComment({ text: "", kind: "note" });
      toast.success("Commentaire ajouté");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const delComment = async (cid) => {
    if (!window.confirm("Supprimer ce commentaire ?")) return;
    try {
      await api.delete(`/admin/companies/${id}/comments/${cid}`);
      toast.success("Supprimé"); load();
    } catch { toast.error("Erreur"); }
  };

  if (!data) return <PortalLayout sidebar="admin"><div className="p-8 text-sm text-neutral-500">Chargement…</div></PortalLayout>;

  const c = data.company;
  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("fr", { dateStyle: "short", timeStyle: "short" }) : "—";

  return (
    <PortalLayout sidebar="admin">
      <div className="space-y-6">
        <Link to="/admin/members" className="inline-flex items-center gap-1 text-xs font-bold text-wine-700 hover:underline"><ArrowLeft className="h-3.5 w-3.5" /> Membres</Link>

        {/* Header */}
        <div className="overflow-hidden rounded-3xl border border-wine-100 bg-white">
          <div className="flex gap-5 p-6">
            <div className="grid h-24 w-24 shrink-0 place-items-center overflow-hidden rounded-2xl border border-wine-100 bg-wine-50">
              <CompanyLogo src={c.logo} name={c.name} size={96} className="rounded-2xl" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <h1 className="font-serif text-3xl text-navy truncate">{c.name}</h1>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-neutral-600">
                    {c.pole && <span className="rounded-full bg-wine-50 px-2.5 py-0.5 font-semibold text-wine-700" data-testid="badge-pole">{poleLabel(c.pole) || c.pole}</span>}
                    {c.category && (() => { const cat = categories.find((x) => x.id === c.category); return cat ? <span className="rounded-full bg-blush-100 px-2.5 py-0.5 font-semibold text-wine-700" data-testid="badge-category"><Tag className="mr-1 inline h-3 w-3" />{cat.name}</span> : null; })()}
                    {c.status && <span className={`rounded-full px-2.5 py-0.5 font-semibold ${c.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-600"}`}>{c.status}</span>}
                    {c.country && <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{c.city}, {c.country}</span>}
                  </div>
                </div>
                {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full border border-neutral-200 px-3 py-1.5 text-xs font-semibold text-wine-700 hover:bg-wine-50"><Globe className="h-3 w-3" /> Site web</a>}
                <button onClick={openEditProfile} className="inline-flex items-center gap-1 rounded-full bg-wine-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-wine-700" data-testid="edit-company-profile-btn"><Edit3 className="h-3 w-3" /> Modifier la fiche</button>
              </div>
              {c.description && <p className="mt-3 text-sm text-neutral-700">{c.description}</p>}
            </div>
          </div>
          {/* Pôle + Catégorie d'adhésion (admin edit) */}
          <div className="grid grid-cols-1 gap-4 border-t border-wine-100 bg-wine-50/20 p-6 sm:grid-cols-[1fr_1fr_auto]" data-testid="company-attrs-block">
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Pôle sectoriel</label>
              <select
                data-testid="company-pole-select"
                value={editAttrs.pole}
                onChange={(e) => setEditAttrs((s) => ({ ...s, pole: e.target.value }))}
                className="w-full rounded-lg border border-wine-200 bg-white px-3 py-2 text-sm focus:border-wine-500 focus:outline-none"
              >
                <option value="">— Aucun —</option>
                {POLES.map((p) => (
                  <option key={p.id} value={p.id}>{p.emoji} {p.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Catégorie d&apos;adhésion</label>
              <select
                data-testid="company-category-select"
                value={editAttrs.category}
                onChange={(e) => setEditAttrs((s) => ({ ...s, category: e.target.value }))}
                className="w-full rounded-lg border border-wine-200 bg-white px-3 py-2 text-sm focus:border-wine-500 focus:outline-none"
              >
                <option value="">— Aucune —</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button
                data-testid="save-company-attrs-btn"
                onClick={saveAttrs}
                disabled={savingAttrs || (editAttrs.pole === (c.pole || "") && editAttrs.category === (c.category || ""))}
                className="inline-flex items-center gap-1.5 rounded-lg bg-wine-600 px-4 py-2 text-sm font-bold text-white hover:bg-wine-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Save className="h-4 w-4" />
                {savingAttrs ? "Sauvegarde…" : "Enregistrer"}
              </button>
            </div>
          </div>

          {/* Sprint 92al — Tags + Projects (read-only quick view, editable via "Modifier" above) */}
          <div className="grid grid-cols-1 gap-4 border-t border-wine-100 bg-white p-6 sm:grid-cols-2" data-testid="company-tags-projects">
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Tags / expertises</label>
              {(c.tags || []).length === 0 ? (
                <p className="text-xs italic text-neutral-400">Aucun tag — cliquez sur « Modifier » pour en ajouter.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="company-tags-list">
                  {c.tags.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 rounded-full bg-wine-100 px-2.5 py-0.5 text-[11px] font-semibold text-wine-700" data-testid={`company-tag-${t}`}>
                      <Tag className="h-3 w-3" /> {t}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-neutral-500">Projets WineTech</label>
              {(c.project_ids || []).length === 0 ? (
                <p className="text-xs italic text-neutral-400">Pas encore inscrit à un projet.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5" data-testid="company-projects-list">
                  {(c.project_ids || []).map((pid) => {
                    const p = projectsCatalog.find((x) => x.id === pid);
                    return p ? (
                      <a key={pid} href={`/projects/${p.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-blush-100 px-2.5 py-0.5 text-[11px] font-semibold text-wine-700 hover:bg-blush-200" data-testid={`company-project-${pid}`}>
                        🚀 {p.title}
                      </a>
                    ) : null;
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 border-t border-wine-100 bg-wine-50/30">
            <div className="px-6 py-4"><div className="text-2xl font-bold text-wine-700">{data.stats.members_count}</div><div className="text-[10px] uppercase tracking-wider text-neutral-500">Membres officiels</div></div>
            <div className="border-l border-wine-100 px-6 py-4"><div className="text-2xl font-bold text-wine-700">{data.stats.extra_contacts_count}</div><div className="text-[10px] uppercase tracking-wider text-neutral-500">Contacts événements</div></div>
            <div className="border-l border-wine-100 px-6 py-4"><div className="text-2xl font-bold text-wine-700">{data.stats.activity_count}</div><div className="text-[10px] uppercase tracking-wider text-neutral-500">Activités</div></div>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[1fr_minmax(280px,360px)]">
          {/* Left column */}
          <div className="space-y-6">
            {/* Contacts */}
            <div className="rounded-2xl border border-neutral-100 bg-white p-6" data-testid="contacts-block">
              <h2 className="text-lg font-bold text-navy inline-flex items-center gap-2"><Users className="h-5 w-5 text-wine-500" /> Contacts</h2>
              <div className="mt-4 grid gap-2">
                {data.members.length === 0 && data.extra_participants.length === 0 && (data.crm_contacts || []).length === 0 && (
                  <div className="rounded-xl bg-neutral-50 px-4 py-6 text-center text-sm text-neutral-500">Aucun contact lié.</div>
                )}
                {data.members.map((m) => (
                  <div key={m.id} className="flex items-center gap-3 rounded-xl border border-emerald-100 bg-emerald-50/30 p-3" data-testid={`member-${m.id}`}>
                    <div className="grid h-9 w-9 place-items-center rounded-full bg-emerald-100 text-xs font-bold text-emerald-700">{(m.name || "?").slice(0, 2).toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-navy truncate">{m.name}</div>
                      <div className="text-xs text-neutral-600 truncate">{m.job_title || "—"} · {m.email}</div>
                    </div>
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-700">Membre</span>
                  </div>
                ))}
                {(data.crm_contacts || []).map((c) => (
                  <div key={c.id} className="flex items-center gap-3 rounded-xl border border-blue-100 bg-blue-50/30 p-3" data-testid={`crm-contact-${c.id}`}>
                    <div className="grid h-9 w-9 place-items-center rounded-full bg-blue-100 text-xs font-bold text-blue-700">{(c.name || "?").slice(0, 2).toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-navy truncate">{c.name} {c.is_primary_contact && <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold text-amber-800">★ Principal</span>}</div>
                      <div className="text-xs text-neutral-600 truncate">{c.job_title || "—"} · {c.email}</div>
                    </div>
                    <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-700">Contact CRM</span>
                  </div>
                ))}
                {data.extra_participants.map((p, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-xl border border-neutral-200 bg-white p-3" data-testid={`extra-${p.email}`}>
                    <div className="grid h-9 w-9 place-items-center rounded-full bg-neutral-200 text-xs font-bold text-neutral-700">{(p.name || "?").slice(0, 2).toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold text-navy truncate">{p.name}</div>
                      <div className="text-xs text-neutral-600 truncate">{p.job_title || "—"} · {p.email}</div>
                    </div>
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">Participant</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Sprint 92ag — Activation drip timeline (first member's email) */}
            {data.members.length > 0 && (
              <MemberActivationTimeline email={data.members[0].email} />
            )}

            {/* Sprint 92ah — Stripe invoices for this company */}
            <InvoicesList endpoint={`/admin/companies/${id}/invoices`} title="Factures Stripe" />

            {/* Activity timeline */}
            <div className="rounded-2xl border border-neutral-100 bg-white p-6" data-testid="activity-block">
              <h2 className="text-lg font-bold text-navy inline-flex items-center gap-2"><Activity className="h-5 w-5 text-wine-500" /> Activité ({data.activity.length})</h2>

              {/* Manual activity form (admin only) */}
              <form onSubmit={addManualActivity} className="mt-4 rounded-xl border border-wine-100 bg-wine-50/40 p-3" data-testid="manual-activity-form">
                <div className="text-[10px] font-bold uppercase text-wine-700">Ajouter une activité (appel, RDV, activation...)</div>
                <div className="mt-2 grid gap-2 sm:grid-cols-[120px_140px_1fr]">
                  <select value={newAct.type} onChange={(e) => setNewAct({ ...newAct, type: e.target.value })} className="rounded-xl border border-neutral-200 bg-white px-2 py-2 text-xs" data-testid="manual-activity-type">
                    {ACTIVITY_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                  <input type="datetime-local" value={newAct.at} onChange={(e) => setNewAct({ ...newAct, at: e.target.value })} className="rounded-xl border border-neutral-200 bg-white px-2 py-2 text-xs" />
                  <input value={newAct.label} onChange={(e) => setNewAct({ ...newAct, label: e.target.value })} placeholder='Ex: Call de qualification 30 min' className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-xs" data-testid="manual-activity-label" />
                </div>
                <textarea value={newAct.notes} onChange={(e) => setNewAct({ ...newAct, notes: e.target.value })} rows={2} placeholder="Notes (optionnel)" className="mt-2 w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-xs" data-testid="manual-activity-notes" />
                <div className="mt-2 flex justify-end">
                  <button type="submit" className="inline-flex items-center gap-1 rounded-full bg-wine-600 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-wine-700" data-testid="manual-activity-submit"><Plus className="h-3 w-3" /> Ajouter</button>
                </div>
              </form>

              <div className="mt-4 space-y-3 max-h-[500px] overflow-y-auto pr-2">
                {data.activity.length === 0 && <div className="text-sm text-neutral-500">Aucune activité enregistrée.</div>}
                {data.activity.map((a, i) => {
                  const m = typeMeta(a.type);
                  const Icon = m.icon;
                  return (
                    <div key={a.id || i} className={`group flex gap-3 rounded-xl border-l-2 ${a.manual ? "border-wine-400 bg-wine-50/30" : "border-neutral-200"} px-3 py-2`} data-testid={`activity-row-${i}`}>
                      <div className={`grid h-7 w-7 shrink-0 place-items-center rounded-full ${m.color}`}>
                        <Icon className="h-3.5 w-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1">
                          <span className={`rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${m.color}`}>{m.label}</span>
                          {a.manual && <span className="rounded-full bg-wine-200 px-1.5 py-0.5 text-[9px] font-bold uppercase text-wine-800">Manuel</span>}
                        </div>
                        <div className="mt-1 text-sm text-neutral-800">{a.label}</div>
                        {a.notes && <div className="mt-1 whitespace-pre-line text-xs italic text-neutral-600">{a.notes}</div>}
                        <div className="mt-1 flex items-center gap-2 text-[11px] text-neutral-500">
                          <Clock className="h-3 w-3" />{fmtDate(a.at)}
                          {a.author_email && <span>· par {a.author_email}</span>}
                        </div>
                      </div>
                      {a.manual && a.id && (
                        <button onClick={() => deleteManualActivity(a.id)} className="opacity-0 group-hover:opacity-100 grid h-7 w-7 place-items-center rounded-full text-neutral-400 hover:bg-rose-50 hover:text-rose-600" title="Supprimer" data-testid={`activity-delete-${a.id}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right column: Comments */}
          <div className="rounded-2xl border border-wine-100 bg-white p-6 lg:sticky lg:top-6 lg:self-start" data-testid="comments-block">
            <h2 className="text-lg font-bold text-navy inline-flex items-center gap-2"><MessageSquare className="h-5 w-5 text-wine-500" /> Commentaires</h2>
            <form onSubmit={addComment} className="mt-4 space-y-2">
              <select value={comment.kind} onChange={(e) => setComment({ ...comment, kind: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-xs">
                {COMMENT_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
              </select>
              <textarea required rows={3} value={comment.text} onChange={(e) => setComment({ ...comment, text: e.target.value })} placeholder="Note interne…" className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="comment-text" />
              <button type="submit" className="w-full rounded-full bg-wine-500 px-4 py-2 text-xs font-bold text-white hover:bg-wine-600" data-testid="comment-add"><Plus className="mr-1 inline h-3 w-3" /> Ajouter</button>
            </form>
            <div className="mt-5 space-y-3 max-h-[600px] overflow-y-auto pr-2">
              {data.comments.length === 0 && <div className="text-xs text-neutral-500">Aucun commentaire.</div>}
              {data.comments.map((cm) => (
                <div key={cm.id} className="rounded-xl border border-neutral-100 bg-neutral-50/40 p-3" data-testid={`comment-${cm.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="rounded-full bg-wine-100 px-2 py-0.5 text-[10px] font-bold text-wine-700">{COMMENT_KINDS.find((k) => k.id === cm.kind)?.label || cm.kind}</span>
                    <button onClick={() => delComment(cm.id)} className="text-neutral-400 hover:text-rose-600"><Trash2 className="h-3 w-3" /></button>
                  </div>
                  <p className="mt-2 text-xs text-neutral-700 whitespace-pre-wrap">{cm.text}</p>
                  <div className="mt-1 text-[10px] text-neutral-500">{cm.author_name} · {fmtDate(cm.created_at)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Edit company profile modal — admin can edit even after validation */}
      {editingProfile && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="company-edit-modal">
          <button className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm" onClick={() => setEditingProfile(null)} aria-label="Fermer" />
          <div className="relative max-h-[88vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-7 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-navy"><Edit3 className="mr-1 inline h-5 w-5 text-wine-600" /> Modifier la fiche entreprise</h2>
              <button onClick={() => setEditingProfile(null)} className="grid h-9 w-9 place-items-center rounded-full text-neutral-500 hover:bg-neutral-100" aria-label="Fermer"><X className="h-5 w-5" /></button>
            </div>
            <p className="mb-3 text-xs text-neutral-500">Le <strong>nom commercial</strong> est affiché partout. L&apos;<strong>entité légale</strong> est utilisée uniquement pour la facturation Stripe.</p>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="text-[10px] font-bold uppercase text-neutral-500">Nom commercial *</label>
                <input value={editingProfile.name} onChange={(e) => setEditingProfile({ ...editingProfile, name: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="edit-company-name" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Entité légale (facturation)</label>
                <input value={editingProfile.legal_name} onChange={(e) => setEditingProfile({ ...editingProfile, legal_name: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="edit-company-legal-name" />
              </div>
              <input value={editingProfile.siren} onChange={(e) => setEditingProfile({ ...editingProfile, siren: e.target.value })} placeholder="SIREN" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm self-end" />
              <input value={editingProfile.vat_number} onChange={(e) => setEditingProfile({ ...editingProfile, vat_number: e.target.value })} placeholder="N° TVA intracom." className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <input type="url" value={editingProfile.website} onChange={(e) => setEditingProfile({ ...editingProfile, website: e.target.value })} placeholder="Site web" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <input type="email" value={editingProfile.email} onChange={(e) => setEditingProfile({ ...editingProfile, email: e.target.value })} placeholder="Email" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <input value={editingProfile.phone} onChange={(e) => setEditingProfile({ ...editingProfile, phone: e.target.value })} placeholder="Téléphone" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <input value={editingProfile.city} onChange={(e) => setEditingProfile({ ...editingProfile, city: e.target.value })} placeholder="Ville" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" />
              <CountrySelect value={editingProfile.country} onSelect={({ name, lang }) => setEditingProfile({ ...editingProfile, country: name, lang })} testId="company-edit-country" />
              <input value={editingProfile.tagline} onChange={(e) => setEditingProfile({ ...editingProfile, tagline: e.target.value })} placeholder="Tagline (1 ligne)" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm md:col-span-2" />
              <textarea rows={4} value={editingProfile.description} onChange={(e) => setEditingProfile({ ...editingProfile, description: e.target.value })} placeholder="Description" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm md:col-span-2" />
              <div className="md:col-span-2 grid gap-3 sm:grid-cols-2">
                <ImageUpload value={editingProfile.logo} onChange={(url) => setEditingProfile({ ...editingProfile, logo: url })} label="Logo (PNG/SVG, format libre, 512px min.)" testId="edit-company-logo" />
                <ImageUpload value={editingProfile.banner} onChange={(url) => setEditingProfile({ ...editingProfile, banner: url })} label="Bannière (16:9, 1600×900 conseillé)" testId="edit-company-banner" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase text-neutral-500">Statut</label>
                <select value={editingProfile.status} onChange={(e) => setEditingProfile({ ...editingProfile, status: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm">
                  <option value="active">Actif</option>
                  <option value="archived">Archivé</option>
                  <option value="pending">En attente</option>
                </select>
              </div>

              {/* Sprint 92al — Tags (free input) + Projects (multi-select from catalog) */}
              <div className="md:col-span-2 rounded-xl border border-neutral-100 bg-neutral-50/50 p-3">
                <label className="text-[10px] font-bold uppercase text-neutral-500">Tags / expertises <span className="text-neutral-400 normal-case font-normal">(virgule pour séparer)</span></label>
                <input
                  value={(editingProfile.tags || []).join(", ")}
                  onChange={(e) => setEditingProfile({ ...editingProfile, tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean) })}
                  placeholder="IoT, Bio, Agroécologie, Export…"
                  className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
                  data-testid="edit-company-tags"
                />
                {(editingProfile.tags || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {editingProfile.tags.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 rounded-full bg-wine-100 px-2 py-0.5 text-[11px] font-semibold text-wine-700" data-testid={`edit-tag-${t}`}>
                        {t}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="md:col-span-2 rounded-xl border border-neutral-100 bg-neutral-50/50 p-3">
                <label className="text-[10px] font-bold uppercase text-neutral-500">Projets WineTech <span className="text-neutral-400 normal-case font-normal">(Ctrl/Cmd + clic pour multi-sélection)</span></label>
                <select
                  multiple
                  value={editingProfile.project_ids || []}
                  onChange={(e) => setEditingProfile({ ...editingProfile, project_ids: Array.from(e.target.selectedOptions).map((o) => o.value) })}
                  className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm"
                  size={Math.min(Math.max(projectsCatalog.length, 3), 6)}
                  data-testid="edit-company-projects"
                >
                  {projectsCatalog.length === 0 && <option disabled>— Aucun projet dans Settings → Projets —</option>}
                  {projectsCatalog.map((p) => (
                    <option key={p.id} value={p.id}>{p.title}{p.status === "archived" ? " (archivé)" : ""}</option>
                  ))}
                </select>
                {(editingProfile.project_ids || []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {editingProfile.project_ids.map((pid) => {
                      const p = projectsCatalog.find((x) => x.id === pid);
                      return p ? <span key={pid} className="inline-flex items-center gap-1 rounded-full bg-wine-500/10 px-2 py-0.5 text-[11px] font-semibold text-wine-700" data-testid={`edit-project-${pid}`}>{p.title}</span> : null;
                    })}
                  </div>
                )}
              </div>
            </div>
            <details className="mt-5 rounded-xl border border-neutral-100 bg-neutral-50 p-3" data-testid="company-diff-history-section">
              <summary className="cursor-pointer text-xs font-bold uppercase tracking-wider text-neutral-600">Historique des modifications</summary>
              <div className="mt-3">
                <DiffHistory entityType="company" entityId={id} />
              </div>
            </details>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditingProfile(null)} className="rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-bold text-neutral-700 hover:bg-neutral-50">Annuler</button>
              <button onClick={saveProfile} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-5 py-2 text-xs font-bold text-white hover:bg-wine-700" data-testid="edit-company-save"><Save className="h-3.5 w-3.5" /> Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </PortalLayout>
  );
}
